<div class="features-table-container">
  <div class="table-header">
    <p-iconfield iconPosition="left">
      <p-inputicon>
        <i class="pi pi-search"></i>
      </p-inputicon>
      <input type="text" pInputText placeholder="Search features..." (input)="onSearch($event)" />
    </p-iconfield>
    <p-button label="Create Feature" icon="pi pi-plus" severity="primary" (onClick)="onCreateFeature()"></p-button>
  </div>

  <p-table
    [scrollable]="true"
    [value]="filteredFeatures()"
    [loading]="isLoading()"
    [paginator]="true"
    [rows]="10"
    [rowsPerPageOptions]="[10, 25, 50]"
    [tableStyle]="{ 'min-width': '50rem' }"
  >
    <ng-template pTemplate="header">
      <tr>
        @for (col of columns(); track col.field) {
          <th [pSortableColumn]="col.sortable ? col.field : undefined">
            {{ col.header }}
            @if (col.sortable) {
              <p-sortIcon></p-sortIcon>
            }
          </th>
        }
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-feature>
      <tr>
        <td>
          <strong>{{ feature.name }}</strong>
        </td>
        <td>
          <code class="code-cell">{{ feature.featureCode || '—' }}</code>
        </td>
        <td>
          <span class="secondary-text">{{ feature.description || 'No description' }}</span>
        </td>
        <td>
          <div class="type-tags">
            @let hasFee = !!feature.oneTimeFee && feature.oneTimeFee > 0;
            @if (!hasFee || feature.featureType !== 'Basic') {
              <p-tag
                [value]="getFeatureTypeLabel(feature.featureType)"
                [severity]="getFeatureTypeSeverity(feature.featureType)"
              ></p-tag>
            }
            @if (hasFee) {
              <p-tag value="Fee" severity="warn"></p-tag>
            }
            @if (feature.isGroupLevel) {
              <p-tag value="Group" severity="info" pTooltip="Assigned at the group level" tooltipPosition="top"></p-tag>
            }
          </div>
        </td>
        <td>
          <strong>{{ formatCurrency(feature.baselinePrice) }}</strong>
        </td>
        <td>
          @if (feature.oneTimeFee && feature.oneTimeFee > 0) {
            <span>{{ formatCurrency(feature.oneTimeFee) }}</span>
          } @else {
            <span class="secondary-text">—</span>
          }
        </td>
        <td>
          <p-tag [value]="getStatusLabel(feature.isActive)" [severity]="getStatusSeverity(feature.isActive)"></p-tag>
        </td>
        <td>{{ formatDate(feature.createdOn) }}</td>
        <td>
          <div class="action-buttons">
            <p-button
              icon="pi pi-pencil"
              severity="info"
              pTooltip="Edit"
              tooltipPosition="top"
              [text]="true"
              [rounded]="true"
              (onClick)="onEditFeature(feature)"
            ></p-button>
            <p-button
              icon="pi pi-trash"
              severity="danger"
              pTooltip="Delete"
              tooltipPosition="top"
              [text]="true"
              [rounded]="true"
              [loading]="deletingId() === feature.featureId"
              (onClick)="onDeleteFeature(feature)"
            ></p-button>
          </div>
        </td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td [attr.colspan]="columns().length">
          <div class="empty-state">
            <i class="pi pi-star large-icon"></i>
            <p>No features found</p>
            @if (searchTerm()) {
              <small>Try adjusting your search criteria</small>
            } @else {
              <small>Create your first feature to get started</small>
            }
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>

<p-dialog
  [header]="dialogTitle()"
  [modal]="true"
  [style]="{ width: '50vw' }"
  [draggable]="false"
  [resizable]="false"
  [(visible)]="showDialog"
>
  <div class="dialog-content">
    @if (isEditMode() && selectedFeatureCode()) {
      <div class="form-field">
        <label for="featureCode">Feature Code</label>
        <input
          class="field-input"
          id="featureCode"
          type="text"
          pInputText
          readonly
          [value]="selectedFeatureCode()"
        />
        <small class="help-text">Auto-generated identifier (read-only)</small>
      </div>
    }

    <div class="form-field">
      <label for="name">Feature Name *</label>
      <input
        class="field-input"
        id="name"
        type="text"
        pInputText
        placeholder="Enter feature name"
        [formField]="featureForm.name"
      />
      @if (featureForm.name().touched() && !featureForm.name().valid()) {
        <small class="p-error">
          @for (error of featureForm.name().errors(); track error.kind) {
            {{ error.message }}
          }
        </small>
      }
    </div>

    <div class="form-field">
      <label for="description">Description</label>
      <textarea
        class="field-input"
        id="description"
        placeholder="Enter feature description"
        rows="3"
        [formField]="featureForm.description"
      ></textarea>
    </div>

    <div class="form-field">
      <label for="categoryCode">Category Code *</label>
      <input
        class="field-input"
        id="categoryCode"
        type="text"
        pInputText
        placeholder="e.g. MKT"
        [formField]="featureForm.categoryCode"
      />
      @if (featureForm.categoryCode().touched() && !featureForm.categoryCode().valid()) {
        <small class="p-error">
          @for (error of featureForm.categoryCode().errors(); track error.kind) {
            {{ error.message }}
          }
        </small>
      }
    </div>

    <div class="form-field">
      <label for="shorthandCode">Shorthand Code *</label>
      <input
        class="field-input"
        id="shorthandCode"
        type="text"
        pInputText
        placeholder="e.g. BANNER"
        [formField]="featureForm.shorthandCode"
      />
      @if (featureForm.shorthandCode().touched() && !featureForm.shorthandCode().valid()) {
        <small class="p-error">
          @for (error of featureForm.shorthandCode().errors(); track error.kind) {
            {{ error.message }}
          }
        </small>
      }
    </div>

    <div class="form-field">
      <label for="featureType">Feature Type *</label>
      <p-select
        class="field-input"
        id="featureType"
        optionLabel="label"
        optionValue="value"
        placeholder="Select feature type"
        [ngModel]="featureFormModel().featureType"
        [options]="featureTypeOptions()"
        (ngModelChange)="onFeatureTypeChange($event)"
      ></p-select>
      <small class="help-text">Basic features are core functionality, Experience features are enhancements</small>
    </div>

    <div class="form-field">
      <label for="baselinePrice">Base Price *</label>
      <p-inputNumber
        class="field-input"
        id="baselinePrice"
        name="baselinePrice"
        mode="currency"
        currency="USD"
        locale="en-US"
        [min]="0"
        [ngModel]="featureForm.baselinePrice().value()"
        (ngModelChange)="featureForm.baselinePrice().value.set($event)"
      ></p-inputNumber>
    </div>

    <div class="form-field">
      <label for="inventory">Inventory</label>
      <p-inputNumber
        class="field-input"
        id="inventory"
        name="inventory"
        [min]="1"
        [showButtons]="true"
        [ngModel]="featureForm.inventory().value()"
        (ngModelChange)="featureForm.inventory().value.set($event)"
      ></p-inputNumber>
      <small class="help-text">Quantity allocated to each membership at purchase time</small>
    </div>

    <div class="form-field">
      <label for="productLine">Product Line</label>
      <p-select
        class="field-input"
        id="productLine"
        optionLabel="label"
        optionValue="value"
        [ngModel]="featureFormModel().productLine"
        [options]="productLineOptions()"
        (ngModelChange)="onProductLineChange($event)"
      ></p-select>
    </div>

    <div class="form-field">
      <label for="pricingFrequency">Pricing Frequency</label>
      <p-select
        class="field-input"
        id="pricingFrequency"
        optionLabel="label"
        optionValue="value"
        [ngModel]="featureFormModel().pricingFrequency"
        [options]="pricingFrequencyOptions()"
        (ngModelChange)="onPricingFrequencyChange($event)"
      ></p-select>
    </div>

    <div class="form-field">
      <label for="usageFrequency">Usage Frequency</label>
      <p-select
        class="field-input"
        id="usageFrequency"
        optionLabel="label"
        optionValue="value"
        [ngModel]="featureFormModel().usageFrequency"
        [options]="usageFrequencyOptions()"
        (ngModelChange)="onUsageFrequencyChange($event)"
      ></p-select>
    </div>

    <div class="form-field">
      <label for="oneTimeFee">One-Time Fee</label>
      <p-inputNumber
        class="field-input"
        id="oneTimeFee"
        name="oneTimeFee"
        mode="currency"
        currency="USD"
        locale="en-US"
        [ngModel]="featureForm.oneTimeFee().value()"
        (ngModelChange)="featureForm.oneTimeFee().value.set($event)"
      ></p-inputNumber>
      <small class="help-text">Optional one-time charge in addition to the base price</small>
    </div>

    <div class="form-field">
      <label for="discountRate">Discount Rate</label>
      <p-inputNumber
        class="field-input"
        id="discountRate"
        name="discountRate"
        [min]="0"
        [minFractionDigits]="0"
        [maxFractionDigits]="2"
        [ngModel]="featureForm.discountRate().value()"
        (ngModelChange)="featureForm.discountRate().value.set($event)"
      ></p-inputNumber>
      <small class="help-text">For a 10% discount, enter 10 (Percentage) or 10 (Flat = $10 off)</small>
    </div>

    <div class="form-field">
      <label for="discountType">Discount Type</label>
      <p-select
        class="field-input"
        id="discountType"
        optionLabel="label"
        optionValue="value"
        [ngModel]="featureFormModel().discountType"
        [options]="discountTypeOptions()"
        (ngModelChange)="onDiscountTypeChange($event)"
      ></p-select>
    </div>

    <div class="form-field checkbox-field">
      <label>
        <input type="checkbox" [formField]="featureForm.isGroupLevel" />
        <span>Group-level (allocated at the household/group instead of per user)</span>
      </label>
    </div>

    <div class="form-field checkbox-field">
      <label>
        <input type="checkbox" [formField]="featureForm.isActive" />
        <span>Active (feature is shown to users)</span>
      </label>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <p-button label="Cancel" severity="secondary" [disabled]="saving()" (onClick)="showDialog.set(false)"></p-button>
    <p-button
      [label]="dialogButtonLabel()"
      [loading]="saving()"
      [disabled]="!isFormValid()"
      (onClick)="onSaveFeature()"
    ></p-button>
  </ng-template>
</p-dialog>
